<template>
  <Teleport to="body">
    <div class="base-notification-parent">
      <TransitionGroup name="transform">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="base-notification"
          :class="{
            [`base-notification--type-${notification.type}`]: true,
            'base-notification--with-hint': Boolean(notification.hint),
          }"
          @click="handleClickOnNotification(notification)"
        >
          <div class="base-notification__wrapper">
            <div v-if="notification.title" class="base-notification__title">
              {{ notification.title }}
            </div>
            <div class="base-notification__message">
              {{ notification.message }}
            </div>
          </div>
          <div class="base-notification__close">
            <BaseButtonIcon
              icon-name="vuesax:close-linear"
              :icon-stroke-width="3"
              size="xxs"
              data-testid="notification-close-button"
              @click.stop="close(notification.id)"
            />
          </div>
          <BaseTypography
            v-if="notification.hint"
            type="caption"
            color="var(--color-surface-placeholder-700)"
            class="base-notification__hint"
          >
            {{ notification.hint }}
          </BaseTypography>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { INotification } from '../../lib/use/notification'

import { copyToClipboard } from '../../lib/text/clipboard'
import { useNotificationStore } from '../../lib/use/notification'
import BaseButtonIcon from '../button-icon/index.vue'
import BaseTypography from '../typography/index.vue'

const notificationStore = useNotificationStore()
const { notifications } = storeToRefs(notificationStore)
const { close } = notificationStore

function handleClickOnNotification(notification: INotification) {
  if (notification.type === 'error' && notification.hint) {
    copyToClipboard(notification.hint)
  }
}
</script>

<style lang="scss" scoped>
.base-notification-parent {
  position: fixed;
  right: rem(10px);
  top: rem(10px);
  z-index: 200000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: rem(6px);
}

.base-notification {
  position: relative;
  max-width: 323px;
  min-width: 200px;
  width: 100%;
  max-height: 112px;
  height: auto;
  padding: rem(20px);
  border-radius: var(--radius-sm);
  box-shadow: 0 0 13.8px 0 #00000040;
  overflow: hidden;
  background-color: var(--color-surface-bg);
  color: var(--color-foreground-regular);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(3px);
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: rem(8px);
  }

  &__close {
    position: absolute;
    top: rem(6px);
    right: rem(6px);

    &:deep(button) {
      background-color: rgb(0 0 0 / 0%);
    }
  }

  &__hint {
    background-color: var(--color-surface-bg);
    position: absolute;
    bottom: rem(4px);
    left: rem(20px);
  }

  &__title {
    @include typo-text-secondary;

    font-weight: 600;
  }

  &__message {
    @include typo-text-mini;
  }

  &--type {
    &-error {
      border-left: 3px solid var(--color-foreground-danger);
    }

    &-success {
      border-left: 3px solid var(--color-foreground-success);
    }

    &-warning {
      border-left: 3px solid var(--color-foreground-warn);
    }
  }

  &--with-hint {
    padding: rem(20px) rem(20px) rem(28px);
  }
}

.transform-enter-active,
.transform-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.transform-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.transform-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
